import React, { useState } from 'react';
import axios from 'axios';
import io from 'socket.io-client';

export default function TestConnection({ streamId, user, onClose }) {
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState([]);

  const addResult = (test, status, message, details = null) => {
    setResults(prev => [...prev, { test, status, message, details, timestamp: Date.now() }]);
  };

  const runFullTest = async () => {
    setTesting(true);
    setResults([]);

    try {
      // Test 1: Backend API Connection
      addResult('API Connection', 'testing', 'Testing backend API...');
      try {
        await axios.get('/api/auth/test', {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        addResult('API Connection', 'success', 'Backend API responding correctly');
      } catch (error) {
        addResult('API Connection', 'error', `API connection failed: ${error.message}`);
      }

      // Test 2: Stream Status Check
      if (streamId) {
        addResult('Stream Status', 'testing', 'Checking stream status...');
        try {
          const response = await axios.get(`/livestream/status/${streamId}`, {
            headers: { Authorization: `Bearer ${user.token}` }
          });
          
          if (response.data.success) {
            const { stream, serverStatus } = response.data;
            addResult('Stream Status', 'success', 'Stream found in database', {
              isActive: stream.isActive,
              hasBroadcaster: serverStatus.hasBroadcaster,
              broadcasterReady: serverStatus.broadcasterReady,
              roomExists: serverStatus.roomExists
            });
          } else {
            addResult('Stream Status', 'error', 'Stream not found or inactive');
          }
        } catch (error) {
          addResult('Stream Status', 'error', `Stream status check failed: ${error.message}`);
        }
      }

      // Test 3: Socket.IO Connection
      addResult('Socket Connection', 'testing', 'Testing socket connection...');
      // Auto-detect backend URL: use env var if set, otherwise detect from current location
      let socketURL = process.env.REACT_APP_BACKEND_URL;
      
      if (!socketURL || socketURL === 'http://localhost:8000') {
        // In production, use same origin (relative URL) or detect from window
        if (typeof window !== 'undefined') {
          const hostname = window.location.hostname;
          
          // Check if it's a production domain (not localhost)
          if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('192.168.')) {
            // Production: use same origin for socket (socket.io will handle it automatically)
            socketURL = '';
            console.log(`🌐 Production domain detected: ${hostname}, using same origin for socket`);
          } else {
            // Development: use localhost:8000
            socketURL = 'http://localhost:8000';
            console.log(`💻 Development mode: using ${socketURL} for socket`);
          }
        } else {
          socketURL = 'http://localhost:8000';
        }
      }
      
      const testSocket = io(socketURL || undefined, {
        withCredentials: true,
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: true
      });

      const socketPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          testSocket.disconnect();
          reject(new Error('Socket connection timeout'));
        }, 10000);

        testSocket.on('connect', () => {
          clearTimeout(timeout);
          addResult('Socket Connection', 'success', 'Socket.IO connection established');
          testSocket.disconnect();
          resolve();
        });

        testSocket.on('connect_error', (error) => {
          clearTimeout(timeout);
          testSocket.disconnect();
          reject(error);
        });
      });

      try {
        await socketPromise;
      } catch (error) {
        addResult('Socket Connection', 'error', `Socket connection failed: ${error.message}`);
      }

      // Test 4: WebRTC Support
      addResult('WebRTC Support', 'testing', 'Checking WebRTC support...');
      if (window.RTCPeerConnection) {
        addResult('WebRTC Support', 'success', 'WebRTC is supported by your browser');
      } else {
        addResult('WebRTC Support', 'error', 'WebRTC is not supported by your browser');
      }

      // Test 5: Network Configuration
      addResult('Network Test', 'testing', 'Testing STUN server connectivity...');
      try {
        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' }
          ]
        });
        
        const iceCandidatePromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            pc.close();
            reject(new Error('ICE gathering timeout'));
          }, 5000);

          pc.onicecandidate = (event) => {
            if (event.candidate) {
              clearTimeout(timeout);
              pc.close();
              addResult('Network Test', 'success', 'STUN server reachable, NAT traversal should work');
              resolve();
            }
          };

          pc.onicegatheringstatechange = () => {
            if (pc.iceGatheringState === 'complete') {
              clearTimeout(timeout);
              pc.close();
              addResult('Network Test', 'warning', 'ICE gathering complete but no candidates found');
              resolve();
            }
          };
        });

        // Create a dummy offer to trigger ICE gathering
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        
        await iceCandidatePromise;
      } catch (error) {
        addResult('Network Test', 'error', `Network test failed: ${error.message}`);
      }

    } catch (error) {
      addResult('General Error', 'error', `Test suite failed: ${error.message}`);
    }

    setTesting(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'testing': return '🔄';
      default: return 'ℹ️';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return '#51cf66';
      case 'error': return '#ff6b6b';
      case 'warning': return '#ffd43b';
      case 'testing': return '#74c0fc';
      default: return '#ced4da';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>🔍 Connection Diagnostic</h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={runFullTest}
            disabled={testing}
            style={{
              backgroundColor: testing ? '#ced4da' : '#1876f2',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: testing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {testing ? '🔄 Running Tests...' : '🚀 Run Full Diagnostic'}
          </button>
        </div>

        <div style={{ minHeight: '200px' }}>
          {results.length === 0 && !testing && (
            <div style={{
              textAlign: 'center',
              color: '#666',
              fontStyle: 'italic',
              padding: '40px'
            }}>
              Click "Run Full Diagnostic" to test your connection
            </div>
          )}

          {results.map((result, index) => (
            <div key={index} style={{
              marginBottom: '12px',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              borderLeft: `4px solid ${getStatusColor(result.status)}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px'
              }}>
                <span style={{ marginRight: '8px', fontSize: '16px' }}>
                  {getStatusIcon(result.status)}
                </span>
                <strong style={{ color: '#333' }}>{result.test}</strong>
              </div>
              <div style={{ color: '#666', fontSize: '14px' }}>
                {result.message}
              </div>
              {result.details && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace'
                }}>
                  {JSON.stringify(result.details, null, 2)}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#1565c0'
        }}>
          <strong>💡 Tips:</strong>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Make sure your backend server is running on the correct port</li>
            <li>Check that WebRTC is enabled in your browser</li>
            <li>If behind a corporate firewall, WebRTC may be blocked</li>
            <li>Try refreshing the page if tests fail</li>
          </ul>
        </div>
      </div>
    </div>
  );
} 